import { Decimal } from 'decimal.js';
// @ts-expect-error - NB: mathjs is not a module
import { type Unit, create, all } from 'mathjs';
import * as Yup from 'yup';

import { fullstoryEvent } from '@frontend/services';

import { Measure, UNITS_BY_SYSTEM_AND_MEASURE } from 'constants/unitOfMeasure';

import { usePreferredUnitSystem } from 'utils/measurement';

/**
 * Gets the decimal precision (number of digits after the decimal point) from a numeric string.
 *
 * @param value - The numeric string to extract precision from.
 * @returns The number of decimal places, or 0 if there are none.
 *
 * @example
 * getPrecision('3.14') // returns 2
 * getPrecision('42') // returns 0
 * getPrecision('.5') // returns 1
 */
export const getPrecision = (
  value: string | number | undefined | null,
): number => {
  if (value == null) return 0;
  return value.toString().split('.')[1]?.length || 0;
};

/**
 * Rounds a number to a specified decimal precision, handling floating point errors.
 *
 * @param num - The number to round.
 * @param precision - The number of decimal places to round to.
 * @returns The rounded number.
 *
 * @example
 * roundToPrecision(3.14159, 2) // returns 3.14
 * roundToPrecision(0.1 + 0.2, 1) // returns 0.3
 */
export const roundToPrecision = (num: number, precision: number): number => {
  const factor = Math.pow(10, precision);
  return Math.round(num * factor) / factor;
};

/**
 * Cleans and normalizes a unit string by:
 * - Replacing Unicode superscript 2 (², ᷲ) with '^2'
 * - Replacing Unicode superscript 3 (³, ᷳ) with '^3'
 * - Removing all non-alphanumeric characters
 *
 * @param unit - The unit string to clean and normalize.
 * @returns The cleaned and normalized unit string.
 */
export const getCleanUnit = (unit: string) => {
  return unit
    .replace(/[\u00B2\u2072]/g, '^2')
    .replace(/[\u00B3\u2073]/g, '^3')
    .replace(/[^a-zA-Z0-9]/g, '');
};

/**
 * Checks if a string contains math operators or unit expressions.
 */
export const isExpression = (value: string): boolean => {
  if (!value) return false;
  // Contains operators or letters (potential units)
  return /[+\-*/()]/.test(value) || /[a-zA-Z]/.test(value);
};

/**
 * Preprocesses a mathematical expression string by handling common unit formatting cases.
 *
 * This function handles:
 * - Mixed numbers with or without units (e.g., "1 1/2" -> "1+1/2", "1 1/2ft" -> "1ft+1/2ft")
 * - Space-separated units (e.g., "2ft 6in", "3m 50cm")
 * - Custom unit formats (sq in, cu in, sq mm, cu mm, etc.)
 *
 * @param expression - The mathematical expression to preprocess
 * @returns The preprocessed expression string
 */
export const preprocessExpression = (expression: string) => {
  return (
    expression
      // Handle mixed numbers with or without units (e.g., "1 1/2" -> "1+1/2", "1 1/2ft" -> "1ft+1/2ft")
      .replace(
        /(\d+)\s+(\d+\/\d+)([a-zA-Z]+)?/g,
        (_, whole, fraction, unit = '') => `${whole}${unit}+${fraction}${unit}`,
      )
      // Handle space-separated units (e.g., "2ft 6in", "3m 50cm")
      .replace(/(\d+[a-zA-Z]+)\s+(\d+[a-zA-Z]+)/g, '$1+$2')
      // Handle custom units
      .replaceAll(/sq in\.?/g, 'sqin')
      .replaceAll(/cu in\.?/g, 'cuin')
      .replaceAll(/sq mm/g, 'sqmm')
      .replaceAll(/cu mm/g, 'cumm')
      .replaceAll(/in\./g, 'in')
      .replaceAll(/mm²/g, 'mm^2')
      .replaceAll(/mm³/g, 'mm^3')
  );
};

/**
 * Evaluates a mathematical expression string, applying custom formatting for specific units.
 *
 * This function replaces certain unit notations in the input expression to match the expected format
 * for the math evaluation library. Supported replacements include:
 * - "sq in." or "sq in" → "sqin"
 * - "cu in." or "cu in" → "cuin"
 * - "sq mm" → "sqmm"
 * - "cu mm" → "cumm"
 * - "in." → "in"
 * - "mm²" → "mm^2"
 * - "mm³" → "mm^3"
 *
 * @param expression - The mathematical expression to evaluate, as a string.
 * @returns The result of the evaluated expression, which may be a number or a Unit.
 */
export const evaluate = (expression: string) => {
  const math = create(all);
  math.config({ number: 'BigNumber', precision: 64 }); // 64 significant digits

  const preprocessedExpression = preprocessExpression(expression);
  return math.evaluate(preprocessedExpression) as Decimal | Unit; // BigNumber support via decimal.js
};

/**
 * Determines whether a given mathematical expression is valid.
 *
 * Attempts to evaluate the provided expression string. If the evaluation
 * succeeds without throwing an error, the expression is considered valid.
 * Otherwise, it is considered invalid.
 *
 * @param expression - The mathematical expression to validate as a string.
 * @returns `true` if the expression is valid, `false` otherwise.
 */
export const getIsValidExpression = (expression: string) => {
  try {
    evaluate(expression);
    return true;
  } catch (error) {
    if (error instanceof Error) {
      fullstoryEvent('mathInputInvalidExpression', {
        expression,
        error: error.message,
      });
    }
    return false;
  }
};

export type UseMathInputValidationSchemaOptions = {
  hasUnits?: boolean;
  positive?: boolean;
  min?: number;
  max?: number;
  lessThan?: number;
};

/**
 * Returns a Yup validation schema for validating math input expressions with units.
 *
 * The schema performs the following validations:
 * 1. Checks if the input is a valid mathematical expression.
 * 2. Ensures the evaluated result is a number or a mathjs Unit.
 * 3. Validates that the unit (if present) is compatible with the expected unit for the given measure and preferred unit system.
 *
 * @param measure - The measure type (e.g., length, area, volume) for which the input should be validated.
 * @returns A Yup string schema with custom tests for math expression validity, numeric result, and unit compatibility.
 */
export const useMathInputValidationSchema = (
  measure: Measure,
  {
    hasUnits = true,
    positive = false,
    min,
    max,
    lessThan,
  }: UseMathInputValidationSchemaOptions = {},
) => {
  const { preferredUnitSystem } = usePreferredUnitSystem();
  const unit = hasUnits
    ? getCleanUnit(UNITS_BY_SYSTEM_AND_MEASURE[preferredUnitSystem][measure])
    : '';

  return Yup.string()
    .test('is-valid-expression', 'Invalid math expression', (value) =>
      getIsValidExpression(value || ''),
    )
    .test('is-number', 'Must be a valid number', (value) => {
      if (value == null) return true; // Allow empty input
      try {
        return Boolean(evaluate(value));
      } catch {
        return false;
      }
    })
    .test('is-valid-unit', 'Invalid unit', (value) => {
      if (value == null) return true; // Allow empty input
      try {
        const result = evaluate(value);
        if (result instanceof Decimal) return true; // No unit for pure numbers
        if (!hasUnits) return false; // No unit for unitless inputs
        try {
          result.toNumeric(unit);
          return true;
        } catch (error) {
          if (error instanceof Error) {
            fullstoryEvent('mathInputInvalidUnit', {
              expression: value,
              error: error.message,
            });
          }
          return false; // Invalid unit conversion
        }
      } catch {
        return false;
      }
    })
    .test('is-positive', 'Must be greater than 0', (value) => {
      if (value == null || !positive) return true;
      try {
        const result = evaluate(value);
        if (result instanceof Decimal) {
          return result.gt(0);
        }
        const evaluatedResult = result.toNumeric(unit);
        if (typeof evaluatedResult === 'number') {
          return evaluatedResult > 0;
        } else {
          return evaluatedResult.gt(0);
        }
      } catch {
        return false;
      }
    })
    .test('is-min', `Must be greater than or equal to ${min}`, (value) => {
      if (value == null || min == null) return true;
      try {
        const result = evaluate(value);
        if (result instanceof Decimal) {
          return result.gte(min);
        }
        const evaluatedResult = result.toNumeric(unit);
        if (typeof evaluatedResult === 'number') {
          return evaluatedResult >= min;
        } else {
          return evaluatedResult.gte(min);
        }
      } catch {
        return false;
      }
    })
    .test('is-max', `Must be less than or equal to ${max}`, (value) => {
      if (value == null || max == null) return true;
      try {
        const result = evaluate(value);
        if (result instanceof Decimal) {
          return result.lte(max);
        }
        const evaluatedResult = result.toNumeric(unit);
        if (typeof evaluatedResult === 'number') {
          return evaluatedResult <= max;
        } else {
          return evaluatedResult.lte(max);
        }
      } catch {
        return false;
      }
    })
    .test('is-less-than', `Must be less than ${lessThan}`, (value) => {
      if (value == null || lessThan == null) return true;
      try {
        const result = evaluate(value);
        if (result instanceof Decimal) {
          return result.lt(lessThan);
        }
        const evaluatedResult = result.toNumeric(unit);
        if (typeof evaluatedResult === 'number') {
          return evaluatedResult < lessThan;
        } else {
          return evaluatedResult.lt(lessThan);
        }
      } catch {
        return false;
      }
    });
};
